import { ReactNode, Ref, useEffect, useRef } from "react";
import cx from "classnames";

import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";

import styles from "../styles/CategoryBrowseRow.module.less";

type CategoryBrowseRowProps = {
  items: { key: number; node: ReactNode }[];
  /** Each tile is as wide as its content instead of a fixed share of the row. */
  isContentWidth?: boolean;
  /** Scrolled into view when off-screen, e.g. a selection from the URL. */
  revealKey: number | null;
  firstCellRef?: Ref<HTMLDivElement>;
};

/** A carousel of tiles, six across. */
const CategoryBrowseRow = ({
  items,
  isContentWidth = false,
  revealKey,
  firstCellRef,
}: CategoryBrowseRowProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (revealKey === null) {
      return;
    }
    const scroller = scrollRef.current;
    const cell = scroller?.querySelector<HTMLElement>(`[data-key="${revealKey}"]`);
    if (!scroller || !cell) {
      return;
    }
    const inset = parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
    const scrollerBox = scroller.getBoundingClientRect();
    const cellBox = cell.getBoundingClientRect();
    if (cellBox.left < scrollerBox.left + inset || cellBox.right > scrollerBox.right) {
      scroller.scrollLeft += cellBox.left - scrollerBox.left - inset;
    }
  }, [revealKey]);

  return (
    <section className={styles.row}>
      <HorizontalOverflow.Contained
        direction="right"
        scrollRef={scrollRef}
        classNames={{ scroller: styles.carousel, button: styles.arrow }}
      >
        {items.map(({ key, node }, index) => (
          <div
            key={key}
            ref={index === 0 ? firstCellRef : undefined}
            className={cx(styles.cell, isContentWidth && styles.contentWidth)}
            data-key={key}
          >
            {node}
          </div>
        ))}
      </HorizontalOverflow.Contained>
    </section>
  );
};

export default CategoryBrowseRow;
